export interface RunController {
  start(): Promise<void>;
  pause(): Promise<void>;
  resume(): Promise<void>;
  stop(): Promise<void>;
  dispose(): void;
}

interface CreateRunControllerArgs {
  recordingId: string;
  noteId?: string;
  onChunk(blob: Blob): Promise<void> | void;
  onError?(error: Error): void;
  onStop?(): void;
  onLevel?(level: number): void;
}

class WebRunController implements RunController {
  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private readonly mimeType = "audio/webm";
  private audioContext: AudioContext | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private isLevelPaused = false;

  constructor(private readonly options: CreateRunControllerArgs) {}

  async start(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Audio recording is not supported in this browser");
    }

    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    const recorder = new MediaRecorder(this.stream, { mimeType: this.mimeType });
    recorder.ondataavailable = event => {
      if (event.data && event.data.size > 0) {
        Promise.resolve(this.options.onChunk(event.data)).catch(error => {
          this.options.onError?.(error instanceof Error ? error : new Error(String(error)));
        });
      }
    };
    recorder.onerror = event => {
      const errorEvent = event as Event & { error?: DOMException };
      const error = errorEvent.error || new DOMException("MediaRecorder error");
      this.options.onError?.(error);
    };
    recorder.onstop = () => {
      this.options.onStop?.();
      this.dispose();
    };

    recorder.start(1_000);
    this.mediaRecorder = recorder;
    this.isLevelPaused = false;

    if (this.options.onLevel) {
      try {
        const audioContext = new AudioContext();
        await audioContext.resume();

        const source = audioContext.createMediaStreamSource(this.stream);
        const processor = audioContext.createScriptProcessor(1024, 1, 1);

        processor.onaudioprocess = event => {
          if (this.isLevelPaused) {
            this.options.onLevel?.(0);
            const silentOutput = event.outputBuffer.getChannelData(0);
            silentOutput.fill(0);
            return;
          }

          const input = event.inputBuffer.getChannelData(0);
          let sum = 0;
          for (let i = 0; i < input.length; i += 1) {
            const sample = input[i];
            sum += sample * sample;
          }
          const rms = Math.sqrt(sum / input.length);
          const normalized = Math.min(1, Math.max(0, rms * 6));
          this.options.onLevel?.(normalized);
          const output = event.outputBuffer.getChannelData(0);
          output.fill(0);
        };

        source.connect(processor);
        processor.connect(audioContext.destination);

        this.audioContext = audioContext;
        this.sourceNode = source;
        this.processor = processor;
      } catch (error) {
        this.options.onError?.(
          error instanceof Error ? error : new Error("Failed to initialize audio meter")
        );
      }
    }
  }

  async pause(): Promise<void> {
    if (this.mediaRecorder?.state === "recording") {
      this.isLevelPaused = true;
      this.options.onLevel?.(0);
      this.mediaRecorder.pause();
    }
  }

  async resume(): Promise<void> {
    if (this.mediaRecorder?.state === "paused") {
      this.isLevelPaused = false;
      this.mediaRecorder.resume();
    }
  }

  async stop(): Promise<void> {
    if (!this.mediaRecorder) return;
    this.isLevelPaused = true;
    this.options.onLevel?.(0);
    if (this.mediaRecorder.state === "inactive") {
      this.options.onStop?.();
      this.dispose();
      return;
    }
    this.mediaRecorder.stop();
  }

  dispose(): void {
    this.isLevelPaused = true;
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    this.mediaRecorder = null;
    if (this.processor) {
      this.processor.disconnect();
      this.processor.onaudioprocess = null;
      this.processor = null;
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    this.options.onLevel?.(0);
  }
}

export const createRunController = (args: CreateRunControllerArgs): RunController => {
  // For now, using this on both desktop and web.
  return new WebRunController(args);
};

export type { CreateRunControllerArgs };
